import { Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Label,
  Switch,
} from "~/components/ui";

interface CategorySchemaMigrationRunDialogProps {
  open: boolean;
  planKey: string;
  jobCount: number;
  processing: boolean;
  onConfirm: (autoApprove: boolean) => void;
  onCancel: () => void;
}

export function CategorySchemaMigrationRunDialog({
  open,
  planKey,
  jobCount,
  processing,
  onConfirm,
  onCancel,
}: CategorySchemaMigrationRunDialogProps) {
  const { t } = useTranslation();
  const autoApproveId = useId();
  const [autoApprove, setAutoApprove] = useState(false);

  const handleCancel = () => {
    setAutoApprove(false);
    onCancel();
  };

  const handleConfirm = () => {
    onConfirm(autoApprove);
    setAutoApprove(false);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !processing) handleCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("feature.categories.recordMigration.runConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("feature.categories.recordMigration.runConfirmDescription", { jobCount })}
          </AlertDialogDescription>
          <div className="break-all font-mono text-12-regular text-fg-3">{planKey}</div>
          <div className="flex items-center justify-between gap-4 rounded-md border border-border-default p-3">
            <div className="space-y-1">
              <Label htmlFor={autoApproveId}>{t("feature.categories.recordMigration.autoApprove")}</Label>
              <p className="text-12-regular text-fg-3">
                {t(
                  autoApprove
                    ? "feature.categories.recordMigration.autoApproveEnabledDescription"
                    : "feature.categories.recordMigration.autoApproveDisabledDescription",
                )}
              </p>
            </div>
            <Switch id={autoApproveId} checked={autoApprove} onCheckedChange={setAutoApprove} disabled={processing} />
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={processing}>{t("ui.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={processing}>
            {processing ? <Loader2 className="animate-spin" /> : null}
            {t(
              processing ? "feature.categories.recordMigration.runStarting" : "feature.categories.recordMigration.run",
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
